import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
} from '@televet/kibble-ui';
import format from 'date-fns/format';
import sortBy from 'lodash-es/sortBy';
import React, { useCallback } from 'react';
import { GraphQLFetchPolicies } from 'shared/enums';
import useClinicUser from 'shared/hooks/useClinicUser';
import {
  AutomatedTriggerType,
  ConditionAttribute,
  GetAppointmentOptionsQuery,
  GetAutomationOverviewQuery,
  GetClinicFormTemplatesQuery,
  useGetAppointmentOptionsLazyQuery,
  useGetAutomationOverviewLazyQuery,
  useGetClinicFormTemplatesLazyQuery,
  WorkflowSendingHours,
} from 'shared/types/graphql';
import { convertObjectArrayToCSV } from 'shared/utils';

const nextStepLiteral = (
  config: { nextWorkflowEventTriggerSettingId?: string; workflowEventTriggerActionNumber: number },
  automationNames: { [automationId: string]: string },
): string => {
  if (config.nextWorkflowEventTriggerSettingId) {
    return automationNames[config.nextWorkflowEventTriggerSettingId];
  }
  if (!config.workflowEventTriggerActionNumber) {
    return 'End Automation';
  }

  return 'Next prompt';
};

const sendTimingLiteral = (
  automatedTrigger?: number | null,
  automatedTriggerType?: AutomatedTriggerType | null,
): string => {
  if (!automatedTrigger || !automatedTriggerType) {
    return '';
  }
  let unitOfMeasure = 'minute';
  if (automatedTrigger % 60 === 0) {
    automatedTrigger = automatedTrigger / 60;
    unitOfMeasure = 'hour';
  }
  if (automatedTrigger > 1) {
    unitOfMeasure += 's';
  }
  return `${automatedTrigger} ${unitOfMeasure} ${automatedTriggerType}`;
};

const SendingHoursLiteral = {
  [WorkflowSendingHours.All24Hours]: 'All 24 Hours',
  [WorkflowSendingHours.BusinessHours]: 'Business Hours',
  [WorkflowSendingHours.AutomationHours]: 'Automation Hours',
};

interface AutomationOverView {
  automationTitle: string;
  automationStatus: string;
  automationType: string;
  appointmentTypes: string;
  providers: string;
  room: string;
  species: string;
  patientAge: string;
  sendTiming: string;
  sendHours: string;
  frequency: string;
  notificationMessage: string;
  prompts: string;
  formsIncluded: string;
  link: string;
}

const overViewDataBuilder = (
  data?: GetAutomationOverviewQuery,
  formsData?: GetClinicFormTemplatesQuery,
  appointmentData?: GetAppointmentOptionsQuery,
): AutomationOverView[] => {
  const formNames: { [formId: string]: string } = {};
  formsData?.findManyFormTemplate?.forEach((form) => {
    formNames[form.id] = form.title;
  });

  const automationNames: { [automationId: string]: string } = {};
  data?.findManyWorkflowEventSetting?.forEach((automation) => {
    automationNames[automation.id] = automation.name;
  });

  const result =
    data?.findManyWorkflowEventSetting?.map((automation) => {
      const formsIncluded = automation.actions
        .filter((action) => action.config.formTemplateId)
        .map((action) => formNames[action.config.formTemplateId])
        .join('\n');

      const linkedAutomations = automation.actions
        .filter((action) => action.config.nextWorkflowEventTriggerSettingId)
        .map((action) => {
          return automationNames[action.config.nextWorkflowEventTriggerSettingId];
        })
        .join(String.fromCharCode(10));
      const appointmentTypeIds = automation.conditionSets
        .map((conditions) =>
          conditions.conditions
            .filter((subCondition) => subCondition.attribute === ConditionAttribute.AppointmentTypeId)
            .map((subCondition) => subCondition.operand.value)
            .flat(),
        )
        .flat();
      const appointmentTypeNames = appointmentTypeIds
        .map((appointmentTypeId) => {
          const appointmentType = appointmentData?.appointmentTypes?.find((appointmentType) => {
            return appointmentType.id === appointmentTypeId;
          });
          return appointmentType?.name || '';
        })
        .join('\n');

      const appointmentProviderIds = automation.conditionSets
        .map((conditions) =>
          conditions.conditions
            .filter((subCondition) => subCondition.attribute === ConditionAttribute.ClinicEmployeeId)
            .map((subCondition) => subCondition.operand.value)
            .flat(),
        )
        .flat();

      const appointmentProviderNames = appointmentProviderIds
        .map((clinicEmployeeId) => {
          const clinicEmployee = appointmentData?.clinicEmployees?.find((clinicEmployee) => {
            return clinicEmployee.id === clinicEmployeeId;
          });
          return clinicEmployee?.firstName || '' + ' ' + clinicEmployee?.lastName || '';
        })
        .join('\n');

      const roomIds = automation.conditionSets
        .map((conditions) =>
          conditions.conditions
            .filter((subCondition) => subCondition.attribute === ConditionAttribute.ClinicRoomId)
            .map((subCondition) => subCondition.operand.value)
            .flat(),
        )
        .flat();

      const roomNames = roomIds
        .map((roomId) => {
          const room = appointmentData?.clinicRooms?.find((room) => {
            return room.id === roomId;
          });
          return room?.name;
        })
        .join('\n');

      return {
        automationTitle: automation.name,
        automationStatus: automation.isPublished ? 'Active' : 'Inactive',
        automationType: automation.event.split('_').join(' '),
        appointmentTypes: appointmentTypeNames || 'none',
        linkedAutomations: linkedAutomations || 'none',
        providers: appointmentProviderNames || 'none',
        room: roomNames || 'none',
        species:
          automation.conditionSets
            .map((condition) =>
              condition.conditions
                .filter((subCondition) => subCondition.attribute === ConditionAttribute.SpeciesType)
                .map((subCondition) => subCondition.operand.value.join(''))
                .join(),
            )
            .join('\n') || 'none',
        patientAge:
          automation.conditionSets
            .map((condition) =>
              condition.conditions
                .filter((subCondition) => subCondition.attribute === ConditionAttribute.PetAgeInYears)
                .map((subCondition) => {
                  return subCondition.operand.value.toString();
                })
                .join(),
            )
            .join('\n') || 'none',
        sendTiming: sendTimingLiteral(automation.automatedTriggerInMinutes, automation.automatedTriggerType),
        sendHours: SendingHoursLiteral[automation.sendingHours],
        frequency: automation.visitType,
        notificationMessage: automation.notificationBody || 'Standard',
        prompts: sortBy(automation.actions, (action) => action.order)
          .map((action) => {
            return `${action.order}. ${action.promptType}\nInitial status change:\n${
              action.channelStatusChange?.name || 'No change'
            }\n\nQuestion or message:\n${action.config.messageBodyTemplate}\n\nNext Step:\n${nextStepLiteral(
              action.config,
              automationNames,
            )}\n`;
          })
          .join('\n'),
        formsIncluded: formsIncluded,
        link: `https://flow.otto.vet/automations/${automation.id}/settings`,
      };
    }) || [];

  return result;
};

const columnHelper = createColumnHelper<AutomationOverView>();

interface TitleHeaderProps {
  title: string;
}

const TitleHeader: React.FC<TitleHeaderProps> = ({ title }) => <Text size="xs">{title}</Text>;
TitleHeader.displayName = 'TitleHeader';

const columns = [
  // eslint-disable-next-line
  // @ts-ignore
  columnHelper.accessor('automationTitle', {
    header: () => TitleHeader({ title: 'Automation Title' }),
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor('automationStatus', {
    header: () => TitleHeader({ title: 'Automation Status' }),
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor('automationType', {
    header: () => TitleHeader({ title: 'Automation Type' }),
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor('linkedAutomations', {
    header: () => TitleHeader({ title: 'Linked Automations' }),
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor('appointmentTypes', {
    header: () => TitleHeader({ title: 'Appointment Types' }),
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor('providers', {
    header: () => TitleHeader({ title: 'Appointment Provider' }),
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor('room', {
    header: () => TitleHeader({ title: 'Appointment Room' }),
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor('species', {
    header: () => TitleHeader({ title: 'Patient Species' }),
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor('patientAge', {
    header: () => TitleHeader({ title: 'Patient Age' }),
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor('sendTiming', {
    header: () => TitleHeader({ title: 'Send Timing' }),
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor('sendHours', {
    header: () => TitleHeader({ title: 'Send Hours' }),
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor('frequency', {
    header: () => TitleHeader({ title: 'Send Frequency' }),
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor('notificationMessage', {
    header: () => TitleHeader({ title: 'Notification Message' }),
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor('prompts', {
    header: () => TitleHeader({ title: 'Prompts' }),
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor('formsIncluded', {
    header: () => TitleHeader({ title: 'Forms Included' }),
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor('link', {
    header: () => TitleHeader({ title: 'Link' }),
    cell: (info) => info.getValue(),
  }),
];

const AutomationsOverView = (): JSX.Element => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { currentClinic } = useClinicUser();
  const clinicName = currentClinic?.name || 'Clinic';
  const [fetchAutomations, { data }] = useGetAutomationOverviewLazyQuery({
    variables: { clinicId: currentClinic?.id || '' },
  });

  const [fetchFormData, { data: formsData }] = useGetClinicFormTemplatesLazyQuery({
    variables: { where: { clinicId: { equals: currentClinic?.id || '' } } },
  });

  const [fetchAppointmentData, { data: appointmentData }] = useGetAppointmentOptionsLazyQuery({
    variables: { clinicId: currentClinic?.id || '' },
  });

  const tableRef = React.useRef<HTMLDivElement>(null);

  const table = useReactTable({
    data: overViewDataBuilder(data, formsData, appointmentData),
    // eslint-disable-next-line
    // @ts-ignore
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  // Blatant "inspiration" from https://codepen.io/Jacqueline34/pen/pyVoWr
  const downloadCSV = useCallback(() => {
    const link = document.createElement('a');

    let csv = convertObjectArrayToCSV(JSON.stringify(overViewDataBuilder(data, formsData, appointmentData)));
    if (csv == null) return;

    const filename = `${clinicName.replaceAll(' ', '_')}_Automations_Overview_${format(
      new Date(),
      "MM-dd-yy'_'_h_mm_aaaaa'm'",
    )}.csv`;

    if (!csv.match(/^data:text\/csv/i)) {
      csv = `data:text/csv;charset=utf-8,${csv}`;
    }

    link.setAttribute('href', encodeURI(csv));
    link.setAttribute('download', filename);
    link.click();
  }, [appointmentData, clinicName, data, formsData]);

  const copyTable = useCallback((): void => {
    if (document.createRange && window.getSelection) {
      const range = document.createRange();
      const sel = window.getSelection();
      sel?.removeAllRanges();
      try {
        range.selectNodeContents(tableRef.current as Node);
        sel?.addRange(range);
        document.execCommand('Copy');
      } catch (e) {
        range.selectNode(tableRef.current as Node);
        sel?.addRange(range);
        document.execCommand('Copy');
      } finally {
        sel?.removeAllRanges();
      }
    }
  }, []);

  return (
    <>
      <Flex gap="3" mb="5">
        <Text>Automations Overview</Text>
        <Button
          size="xs"
          onClick={(): void => {
            fetchAutomations({ fetchPolicy: GraphQLFetchPolicies.NoCache });
            fetchFormData({ fetchPolicy: GraphQLFetchPolicies.NoCache });
            fetchAppointmentData({ fetchPolicy: GraphQLFetchPolicies.NoCache });
            onOpen();
          }}
        >
          Open
        </Button>
      </Flex>
      <Modal size="xxl" isOpen={isOpen} onClose={onClose}>
        <ModalContent>
          <ModalHeader>
            <Flex justifyContent="space-between">
              <>{clinicName} Automations Overview</>
              <Flex>
                <Button size="xs" onClick={copyTable} mr="6">
                  Copy Table
                </Button>
                <Button size="xs" onClick={downloadCSV} mr="6">
                  Download CSV
                </Button>
              </Flex>
            </Flex>
            <ModalCloseButton />
          </ModalHeader>
          <ModalBody>
            <Flex maxH="75vh" overflow="auto">
              <TableContainer ref={tableRef} overflowX="unset" overflowY="unset">
                <Table variant="striped" size="sm">
                  <Thead position="sticky" top={0} zIndex="docked" backgroundColor="background.default">
                    {table.getHeaderGroups().map((headerGroup) => {
                      return (
                        <Tr key={headerGroup.id}>
                          {headerGroup.headers.map((header) => (
                            <Th key={header.id}>
                              {header.isPlaceholder
                                ? null
                                : flexRender(header.column.columnDef.header, header.getContext())}
                            </Th>
                          ))}
                        </Tr>
                      );
                    })}
                  </Thead>

                  <Tbody>
                    {table.getRowModel().rows.map((row) => (
                      <Tr key={row.id}>
                        {row.getVisibleCells().map((cell) => {
                          return (
                            <Td key={cell.id} whiteSpace="pre-wrap">
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </Td>
                          );
                        })}
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default AutomationsOverView;
